import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}



/**
 * 从完整的HTML+CSS代码中提取HTML部分
 */
export function extractHtmlFromCode(fullCode: string): string {
  try {
    if (!fullCode) return '';
    
    let html = fullCode;
    
    // 移除<style>标签内容
    html = html.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '');
    
    // 移除<script>标签内容（保留HTML内的onclick等）
    html = html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
    
    // 移除HTML注释
    html = html.replace(/<!--[\s\S]*?-->/g, '');
    
    // 移除多余的空行
    html = html.replace(/\n\s*\n/g, '\n');
    
    return html.trim();
  } catch (error) {
    console.error('提取HTML失败:', error);
    return fullCode;
  }
}

/**
 * 从完整的HTML+CSS代码中提取CSS部分
 */
export function extractCssFromCode(fullCode: string): string {
  try {
    if (!fullCode) return '';
    
    let css = '';
    
    // 提取所有<style>标签内容
    const styleRegex = /<style[\s\S]*?>([\s\S]*?)<\/style>/gi;
    let match;
    
    while ((match = styleRegex.exec(fullCode)) !== null) {
      if (match[1]) {
        css += match[1].trim() + '\n';
      }
    }
    
    // 提取内联样式（备用）
    if (!css) {
      const inlineStyleRegex = /style\s*=\s*["']([^"']*)["']/gi;
      const inlineStyles: string[] = [];
      
      while ((match = inlineStyleRegex.exec(fullCode)) !== null) {
        if (match[1]) {
          inlineStyles.push(match[1]);
        }
      }
      
      if (inlineStyles.length > 0) {
        css = '/* 内联样式提取 */\n' + inlineStyles.join('\n');
      }
    }
    
    return css.trim();
  } catch (error) {
    console.error('提取CSS失败:', error);
    return '';
  }
}

/**
 * 检查代码是否包含有效的CSS
 */
export function hasValidCss(code: string): boolean {
  return code.includes('<style') || code.includes('style=') || 
         code.includes('{') && code.includes('}') && code.includes(':');
}

/**
 * 为预览生成完整的HTML文档
 */
export function generatePreviewHtml(htmlCode: string, cssCode: string, deviceType: string = 'desktop'): string {
  const maxWidth = deviceType === 'mobile' ? '375px' : 
                   deviceType === 'tablet' ? '768px' : '1200px';
  
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>代码预览</title>
  <style>
    /* 基础样式 */
    body { 
      margin: 0; 
      padding: 20px; 
      background: #f8fafc; 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.5;
    }
    
    /* 预览容器 */
    .preview-container { 
      max-width: ${maxWidth};
      margin: 0 auto;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      transition: all 0.3s ease;
    }
    
    /* 生成的CSS */
    ${cssCode}
    
    /* 响应式调整 */
    @media (max-width: 768px) {
      body { padding: 10px; }
      .preview-container {
        border-radius: 8px;
      }
    }
    
    /* 移动端特殊处理 */
    @media (max-width: 480px) {
      .preview-container {
        border-radius: 0;
        box-shadow: none;
      }
    }
  </style>
</head>
<body>
  <div class="preview-container">
    ${htmlCode}
  </div>
</body>
</html>`;
}

/**
 * 简化CSS用于uni-app兼容性检查
 */
export function simplifyCssForUniapp(css: string): string {
  if (!css) return '';
  
  let simplified = css;
  
  // uni-app不支持的属性（示例）
  const unsupportedProperties = [
    /content:\s*["'][^"']*["']/g, // content属性
    /position:\s*fixed/g, // 固定定位（在某些平台）
    /float:\s*(left|right)/g, // 浮动
  ];
  
  // 替换或移除不支持的属性
  unsupportedProperties.forEach(pattern => {
    simplified = simplified.replace(pattern, '/* uni-app不支持此属性 */');
  });
  
  // 将px转换为rpx（粗略转换）
  simplified = simplified.replace(/(\d+)px/g, (match, p1) => {
    const pxValue = parseInt(p1, 10);
    return `${pxValue * 2}rpx`;
  });
  
  return simplified;
}
